import { JsonSchemaTree } from './JsonSchemaTree';
import type { StoryMeta } from '@features/devtools/registry/types';

type Props = React.ComponentProps<typeof JsonSchemaTree>;

const sampleSchema = {
  type: 'object',
  title: 'User Record',
  description: 'Represents a user with profile information.',
  required: ['id', 'email'],
  properties: {
    id: {
      type: 'string',
      format: 'uuid',
      description: 'Unique identifier for the user.',
    },
    email: {
      type: 'string',
      format: 'email',
      description: 'Primary email address.',
    },
    primaryEmail: {
      type: 'string',
      format: 'email',
      description: 'Designated primary email.',
    },
    profile: {
      type: 'object',
      description: 'Optional profile details.',
      properties: {
        age: {
          type: 'integer',
          minimum: 0,
          description: 'Age in years.',
        },
        status: {
          type: 'string',
          enum: ['active', 'pending', 'disabled', 'paused'],
          description: 'Lifecycle status.',
        },
        tags: {
          type: 'array',
          description: 'Labels applied to the user.',
          items: {
            type: 'string',
            minLength: 1,
          },
        },
      },
    },
  },
} satisfies Props['schema'];

const meta: StoryMeta<Props> = {
  id: 'output/JsonSchemaTree',
  title: 'JsonSchemaTree',
  group: 'Shared/Data',
  component: JsonSchemaTree,
  description: 'Tree explorer that summarises JSON Schema structure and constraints.',
  defaultProps: {
    schema: sampleSchema,
    defaultExpandedDepth: 2,
    mode: 'standard',
  },
  controls: {
    schema: { type: 'json', label: 'Schema' },
    defaultExpandedDepth: { type: 'number', min: 0, max: 6, step: 1 },
    maxDepth: { type: 'number', min: 1, max: 20, step: 1 },
    mode: {
      type: 'select',
      options: [
        { label: 'Standard', value: 'standard' },
        { label: 'Input', value: 'input' },
        { label: 'Output', value: 'output' },
      ],
    },
  },
  variants: [
    {
      name: 'Input Pins',
      props: {
        mode: 'input',
      },
    },
    {
      name: 'Output Pins',
      props: {
        mode: 'output',
      },
    },
    {
      name: 'Array of Primitives (No Toggle)',
      props: {
        schema: {
          type: 'object',
          properties: {
            tags: { type: 'array', items: { type: 'string' }, minItems: 0 },
            flags: { type: 'array', items: { type: 'boolean' } },
            scores: { type: 'array', items: { type: 'number' } },
          },
        },
        defaultExpandedDepth: 2,
        mode: 'standard',
      },
    },
    {
      name: 'Nested Objects',
      props: {
        schema: {
          type: 'object',
          required: ['workflow'],
          properties: {
            workflow: {
              type: 'object',
              required: ['steps'],
              description: 'Workflow configuration',
              properties: {
                steps: {
                  type: 'array',
                  minItems: 1,
                  items: {
                    type: 'object',
                    required: ['id', 'type'],
                    properties: {
                      id: { type: 'string' },
                      type: { type: 'string', enum: ['task', 'delay', 'branch'] },
                      config: {
                        type: 'object',
                        additionalProperties: true,
                        description: 'Custom step configuration',
                      },
                    },
                  },
                },
              },
            },
            metadata: {
              type: 'object',
              additionalProperties: {
                type: 'string',
              },
            },
          },
        },
      },
    },
  ],
};

export default meta;
